import { ApprovedSubmissionLocation, Task } from '../../types';
import { DraftSaveContext, TaskSessionActor } from './taskSessionController';

export type ReviewControllerAction =
  | 'approve'
  | 'requestChanges'
  | 'escalate'
  | 'rework'
  | 'returnToQueue'
  | 'claimReview'
  | 'releaseReview';

export interface ReviewControllerPorts {
  saveDraft: (context: DraftSaveContext) => Promise<void>;
  approveTask: (
    taskId: string,
    approvedSubmission: ApprovedSubmissionLocation | undefined,
    expectedReviewRound: number,
  ) => Promise<void>;
  requestChanges: (
    taskId: string,
    comment: string,
    expectedReviewRound: number,
  ) => Promise<void>;
  escalateReview: (
    taskId: string,
    note: string,
    expectedReviewRound: number,
  ) => Promise<void>;
  reworkApprovedTask: (taskId: string, expectedReviewRound: number) => Promise<void>;
  returnSubmittedTaskToQueue: (
    taskId: string,
    actor: TaskSessionActor,
    expectedReviewRound: number,
  ) => Promise<void>;
  claimReview: (
    taskId: string,
    actor: TaskSessionActor,
    expectedReviewRound: number,
  ) => Promise<void>;
  releaseReview: (taskId: string, expectedReviewRound: number) => Promise<void>;
  fetchLatestTask: (taskId: string) => Promise<Task | null>;
  applyTaskSnapshot: (task: Task | null) => void;
  reportError?: (error: Error, action: ReviewControllerAction, taskId: string) => void;
}

export interface ReviewControllerContext {
  taskId: string | undefined;
  reviewRound: number;
  actor: TaskSessionActor | null;
  canEditReviewResult: boolean;
  draftSaveContext: DraftSaveContext;
  approvedSubmission?: ApprovedSubmissionLocation;
}

export interface ReviewControllerSnapshot {
  pendingAction: ReviewControllerAction | null;
  error: string | null;
}

interface CapturedContext {
  taskId: string;
  reviewRound: number;
  actor: TaskSessionActor | null;
  canEditReviewResult: boolean;
  draftSaveContext: DraftSaveContext;
  approvedSubmission: ApprovedSubmissionLocation | undefined;
}

const initialSnapshot: ReviewControllerSnapshot = {
  pendingAction: null,
  error: null,
};

function asError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}

/**
 * Owns reviewer/owner review commands while leaving task-session persistence
 * and page composition to their respective boundaries. The command context
 * is captured at invocation time, so a later task switch cannot apply a
 * fetched snapshot or an error to the new page.
 */
export class ReviewController {
  private readonly ports: ReviewControllerPorts;

  private snapshot: ReviewControllerSnapshot = { ...initialSnapshot };

  private listeners = new Set<() => void>();

  private taskId: string | undefined;

  private reviewRound = 0;

  private contextGeneration = 0;

  private disposed = false;

  constructor(ports: ReviewControllerPorts) {
    this.ports = ports;
  }

  getSnapshot = (): ReviewControllerSnapshot => this.snapshot;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  updateContext(taskId: string | undefined, reviewRound: number | undefined): void {
    const changed = taskId !== this.taskId;
    this.taskId = taskId;
    this.reviewRound = reviewRound ?? 0;
    if (!changed) return;

    this.contextGeneration += 1;
    this.snapshot = { ...initialSnapshot };
    this.emit();
  }

  async dispose(): Promise<void> {
    this.contextGeneration += 1;
    this.disposed = true;
    this.snapshot = { ...initialSnapshot };
    this.emit();
  }

  clearError(): void {
    if (!this.snapshot.error) return;
    this.snapshot = { ...this.snapshot, error: null };
    this.emit();
  }

  async approve(context: ReviewControllerContext): Promise<boolean> {
    return this.execute('approve', context, async (captured) => {
      if (captured.canEditReviewResult) {
        await this.ports.saveDraft(captured.draftSaveContext);
      }
      await this.ports.approveTask(
        captured.taskId,
        captured.approvedSubmission,
        captured.reviewRound,
      );
    });
  }

  async requestChanges(context: ReviewControllerContext, comment: string): Promise<boolean> {
    return this.execute('requestChanges', context, async (captured) => {
      if (captured.canEditReviewResult) {
        await this.ports.saveDraft(captured.draftSaveContext);
      }
      await this.ports.requestChanges(
        captured.taskId,
        comment.trim(),
        captured.reviewRound,
      );
    });
  }

  async escalate(context: ReviewControllerContext, note: string): Promise<boolean> {
    const trimmed = note.trim();
    if (!trimmed) return false;
    return this.execute('escalate', context, async (captured) => {
      if (captured.canEditReviewResult) {
        await this.ports.saveDraft(captured.draftSaveContext);
      }
      await this.ports.escalateReview(captured.taskId, trimmed, captured.reviewRound);
    });
  }

  async rework(context: ReviewControllerContext): Promise<boolean> {
    return this.execute('rework', context, (captured) => (
      this.ports.reworkApprovedTask(captured.taskId, captured.reviewRound)
    ));
  }

  async returnToQueue(context: ReviewControllerContext): Promise<boolean> {
    if (!context.actor) return false;
    return this.execute('returnToQueue', context, (captured) => (
      this.ports.returnSubmittedTaskToQueue(
        captured.taskId,
        captured.actor as TaskSessionActor,
        captured.reviewRound,
      )
    ));
  }

  async claimForReview(context: ReviewControllerContext): Promise<boolean> {
    if (!context.actor) return false;
    return this.execute('claimReview', context, async (captured, generation) => {
      await this.ports.claimReview(
        captured.taskId,
        captured.actor as TaskSessionActor,
        captured.reviewRound,
      );
      const fresh = await this.ports.fetchLatestTask(captured.taskId);
      if (!this.isCurrent(generation, captured.taskId)) return;
      this.ports.applyTaskSnapshot(fresh);
    });
  }

  async releaseFromReview(context: ReviewControllerContext): Promise<boolean> {
    return this.execute('releaseReview', context, (captured) => (
      this.ports.releaseReview(captured.taskId, captured.reviewRound)
    ));
  }

  private async execute(
    action: ReviewControllerAction,
    context: ReviewControllerContext,
    operation: (captured: CapturedContext, generation: number) => Promise<void>,
  ): Promise<boolean> {
    if (this.disposed || this.snapshot.pendingAction) return false;
    const captured = this.capture(context);
    if (!captured) return false;
    const generation = this.contextGeneration;
    this.snapshot = { pendingAction: action, error: null };
    this.emit();
    try {
      await operation(captured, generation);
      return this.isCurrent(generation, captured.taskId);
    } catch (error) {
      if (this.isCurrent(generation, captured.taskId)) {
        const normalized = asError(error);
        this.snapshot = { ...this.snapshot, error: normalized.message };
        const claimConflict = action === 'claimReview'
          && 'commandCode' in normalized
          && normalized.commandCode === 'reviewer-already-claimed';
        if (claimConflict) {
          try {
            const fresh = await this.ports.fetchLatestTask(captured.taskId);
            if (!this.isCurrent(generation, captured.taskId)) return false;
            this.ports.applyTaskSnapshot(fresh);
          } catch (refreshError) {
            if (!this.isCurrent(generation, captured.taskId)) return false;
            this.ports.reportError?.(asError(refreshError), action, captured.taskId);
          }
        } else {
          this.ports.reportError?.(normalized, action, captured.taskId);
        }
        this.emit();
      }
      return false;
    } finally {
      if (this.isCurrent(generation, captured.taskId)) {
        this.snapshot = { ...this.snapshot, pendingAction: null };
        this.emit();
      }
    }
  }

  private capture(context: ReviewControllerContext): CapturedContext | null {
    const taskId = context.taskId ?? this.taskId;
    if (!taskId) return null;
    return {
      taskId,
      reviewRound: context.reviewRound,
      actor: context.actor,
      canEditReviewResult: context.canEditReviewResult,
      draftSaveContext: context.draftSaveContext,
      approvedSubmission: context.approvedSubmission,
    };
  }

  private isCurrent(generation: number, taskId: string): boolean {
    return !this.disposed
      && generation === this.contextGeneration
      && taskId === this.taskId;
  }

  private emit(): void {
    this.listeners.forEach((listener) => listener());
  }
}
